import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AmenityCategory } from "@/utils/hotelAmenities";
import { useTranslation } from "react-i18next";

interface HotelAmenitiesProps {
  categories: AmenityCategory[];
}

// Amenity name translations
const amenityTranslations: Record<string, Record<string, string>> = {
  // Amenity names
  'Wi-Fi': { es: 'Wi-Fi', en: 'Wi-Fi', 'pt-BR': 'Wi-Fi' },
  'Internet': { es: 'Internet', en: 'Internet', 'pt-BR': 'Internet' },
  'Estacionamiento': { es: 'Estacionamiento', en: 'Parking', 'pt-BR': 'Estacionamento' },
  'Garaje': { es: 'Garaje', en: 'Garage', 'pt-BR': 'Garagem' },
  'Piscina': { es: 'Piscina', en: 'Pool', 'pt-BR': 'Piscina' },
  'Restaurante': { es: 'Restaurante', en: 'Restaurant', 'pt-BR': 'Restaurante' },
  'Bar': { es: 'Bar', en: 'Bar', 'pt-BR': 'Bar' },
  'Café': { es: 'Café', en: 'Café', 'pt-BR': 'Café' },
  'Desayuno': { es: 'Desayuno', en: 'Breakfast', 'pt-BR': 'Café da manhã' },
  'Gimnasio': { es: 'Gimnasio', en: 'Gym', 'pt-BR': 'Academia' },
  'Centro de fitness': { es: 'Centro de fitness', en: 'Fitness center', 'pt-BR': 'Centro de fitness' },
  'Spa': { es: 'Spa', en: 'Spa', 'pt-BR': 'Spa' },
  'Sauna': { es: 'Sauna', en: 'Sauna', 'pt-BR': 'Sauna' },
  'Masajes': { es: 'Masajes', en: 'Massage', 'pt-BR': 'Massagem' },
  'Aire acondicionado': { es: 'Aire acondicionado', en: 'Air conditioning', 'pt-BR': 'Ar condicionado' },
  'TV': { es: 'TV', en: 'TV', 'pt-BR': 'TV' },
  'Minibar': { es: 'Minibar', en: 'Minibar', 'pt-BR': 'Minibar' },
  'Nevera': { es: 'Nevera', en: 'Refrigerator', 'pt-BR': 'Geladeira' },
  'Recepción 24h': { es: 'Recepción 24h', en: '24h reception', 'pt-BR': 'Recepção 24h' },
  'Recepción': { es: 'Recepción', en: 'Reception', 'pt-BR': 'Recepção' },
  'Conserjería': { es: 'Conserjería', en: 'Concierge', 'pt-BR': 'Concierge' },
  'Servicio de habitaciones': { es: 'Servicio de habitaciones', en: 'Room service', 'pt-BR': 'Serviço de quarto' },
  'Lavandería': { es: 'Lavandería', en: 'Laundry', 'pt-BR': 'Lavanderia' },
  'Seguridad 24h': { es: 'Seguridad 24h', en: '24h security', 'pt-BR': 'Segurança 24h' },
  'Caja fuerte': { es: 'Caja fuerte', en: 'Safe', 'pt-BR': 'Cofre' },
  'Accesible': { es: 'Accesible', en: 'Accessible', 'pt-BR': 'Acessível' },
  'Acceso silla de ruedas': { es: 'Acceso silla de ruedas', en: 'Wheelchair access', 'pt-BR': 'Acesso para cadeirante' },
  'Se admiten mascotas': { es: 'Se admiten mascotas', en: 'Pets allowed', 'pt-BR': 'Aceita animais' },
  'No fumadores': { es: 'No fumadores', en: 'Non-smoking', 'pt-BR': 'Não fumante' },
  'Apto para niños': { es: 'Apto para niños', en: 'Child-friendly', 'pt-BR': 'Apto para crianças' },
  'Familiar': { es: 'Familiar', en: 'Family-friendly', 'pt-BR': 'Para família' },
  'Salón de eventos': { es: 'Salón de eventos', en: 'Event hall', 'pt-BR': 'Salão de eventos' },
  'Salas de conferencias': { es: 'Salas de conferencias', en: 'Conference rooms', 'pt-BR': 'Salas de conferência' },
  'Salas de reuniones': { es: 'Salas de reuniones', en: 'Meeting rooms', 'pt-BR': 'Salas de reunião' },
};

// Category name translations
const categoryTranslations: Record<string, Record<string, string>> = {
  'Conectividad': { es: 'Conectividad', en: 'Connectivity', 'pt-BR': 'Conectividade' },
  'Transporte': { es: 'Transporte', en: 'Transportation', 'pt-BR': 'Transporte' },
  'Ocio': { es: 'Ocio', en: 'Leisure', 'pt-BR': 'Lazer' },
  'Comida y bebida': { es: 'Comida y bebida', en: 'Food & Drink', 'pt-BR': 'Comida e bebida' },
  'Bienestar': { es: 'Bienestar', en: 'Wellness', 'pt-BR': 'Bem-estar' },
  'Habitación': { es: 'Habitación', en: 'Room', 'pt-BR': 'Quarto' },
  'Servicios': { es: 'Servicios', en: 'Services', 'pt-BR': 'Serviços' },
  'Seguridad': { es: 'Seguridad', en: 'Security', 'pt-BR': 'Segurança' },
  'Accesibilidad': { es: 'Accesibilidad', en: 'Accessibility', 'pt-BR': 'Acessibilidade' },
  'Políticas': { es: 'Políticas', en: 'Policies', 'pt-BR': 'Políticas' },
  'Familiar': { es: 'Familiar', en: 'Family', 'pt-BR': 'Familiar' },
  'Eventos': { es: 'Eventos', en: 'Events', 'pt-BR': 'Eventos' },
};

const translateAmenity = (name: string, lang: string): string => {
  const normalizedLang = lang.startsWith('pt') ? 'pt-BR' : lang.startsWith('en') ? 'en' : 'es';
  return amenityTranslations[name]?.[normalizedLang] || name;
};

const translateCategory = (name: string, lang: string): string => {
  const normalizedLang = lang.startsWith('pt') ? 'pt-BR' : lang.startsWith('en') ? 'en' : 'es';
  return categoryTranslations[name]?.[normalizedLang] || name;
};

export const HotelAmenities = ({ categories }: HotelAmenitiesProps) => {
  const { t, i18n } = useTranslation();

  if (categories.length === 0) {
    return null;
  }

  return (
    <Card className="animate-fade-in">
      <CardContent className="p-6">
        <h2 className="text-2xl font-bold mb-4">{t('hotelDetail.servicesAndFacilities')}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category, categoryIndex) => (
            <div 
              key={category.name}
              className="animate-fade-in"
              style={{ animationDelay: `${categoryIndex * 100}ms` }}
            >
              <h3 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide">
                {translateCategory(category.name, i18n.language)}
              </h3>
              
              <div className="flex flex-wrap gap-2">
                {category.amenities.map((amenity) => {
                  const Icon = amenity.icon;
                  return (
                    <Badge
                      key={amenity.name}
                      variant="secondary"
                      className="flex items-center gap-1.5 py-1.5 px-3"
                    >
                      <Icon className="h-3.5 w-3.5" />
                      <span className="text-xs">{translateAmenity(amenity.name, i18n.language)}</span>
                    </Badge>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
